import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, { isValidElement, useCallback } from "react";
import classNames from "classnames";
import { Button, Classes, Checkbox, Icon } from "@blueprintjs/core";
import {
  set,
  toString,
  camelCase,
  startCase,
  noop,
  cloneDeep,
  get,
  padStart,
  flatMap
} from "lodash-es";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import joinUrl from "url-join";
import InfoHelper from "../InfoHelper";
import {
  getEntityIdToEntity,
  getFieldPathToIndex,
  getFieldPathToField,
  getIdOrCodeOrIndex,
  getNumberStrAtEnd,
  getSelectedRowsFromEntities,
  PRIMARY_SELECTED_VAL,
  stripNumberAtEnd
} from "./utils";
import FilterAndSortMenu from "./FilterAndSortMenu";
import { ColumnFilterMenu } from "./ColumnFilterMenu";
import getTextFromEl from "../utils/getTextFromEl";
import rowClick, { finalizeSelection } from "./utils/rowClick";
import { editCellHelper } from "./editCellHelper";
import { getCellVal } from "./getCellVal";
import { useDispatch } from "react-redux";
import { change as _change } from "redux-form";
import { RenderCell } from "./RenderCell";
import { getCCDisplayName } from "./utils/tableQueryParamsToHasuraClauses";
import { showContextMenu } from "../utils/menuUtils";
import { dragNoticeEl } from "./dragNoticeEl";
dayjs.extend(localizedFormat);
const RenderColumnHeader = ({
  recordIdToIsVisibleMap,
  setRecordIdToIsVisibleMap,
  addFilters,
  column,
  compact,
  currentParams,
  entities,
  extraCompact,
  filters,
  resetDefaultVisibility,
  onlyOneVisibleColumn,
  formName,
  isCellEditable,
  updateColumnVisibility,
  schema,
  withDisplayOptions,
  isLocalCall,
  order,
  removeSingleFilter,
  setNewParams,
  setOrder,
  updateEntitiesHelper,
  withFilter,
  withSort
}) => {
  const {
    displayName,
    description,
    isUnique,
    sortDisabled,
    filterDisabled,
    columnFilterDisabled,
    renderTitleInner,
    filterIsActive = noop,
    noTitle,
    isNotEditable,
    type,
    path,
    columnHeader
  } = column;
  const columnDataType = column.type;
  const isActionColumn = columnDataType === "action";
  const disableSorting = sortDisabled || isActionColumn || !isLocalCall && typeof path === "string" && path.includes(".") || columnDataType === "color";
  const disableFiltering = filterDisabled || columnDataType === "color" || isActionColumn || columnFilterDisabled;
  const ccDisplayName = getCCDisplayName(column);
  let columnTitle = displayName || startCase(camelCase(path));
  if (isActionColumn) columnTitle = "";
  const currentFilter = filters && !!filters.length && filters.filter(({ filterOn }) => {
    return filterOn === ccDisplayName;
  })[0];
  const filterActiveForColumn = !!currentFilter || filterIsActive(currentParams);
  let ordering;
  if (order && order.length) {
    order.forEach((order2) => {
      const orderField = order2.replace("-", "");
      if (orderField === path) {
        if (orderField === order2) {
          ordering = "asc";
        } else {
          ordering = "desc";
        }
      }
    });
  }
  const sortDown = ordering && ordering === "asc";
  const sortUp = ordering && !sortDown;
  const FilterMenu = column.FilterMenu || FilterAndSortMenu;
  let maybeCheckbox;
  if (isCellEditable && !isNotEditable && type === "boolean") {
    let isIndeterminate = false;
    let isChecked = !!entities.length;
    let hasFalse;
    let hasTrue;
    entities.some((e) => {
      if (!get(e, path)) {
        isChecked = false;
        hasFalse = true;
      } else {
        hasTrue = true;
      }
      if (hasFalse && hasTrue) {
        isIndeterminate = true;
        return true;
      }
      return false;
    });
    maybeCheckbox = /* @__PURE__ */ jsx(
      Checkbox,
      {
        style: { marginBottom: 0, marginLeft: 3 },
        onChange: () => {
          updateEntitiesHelper(entities, (ents) => {
            ents.forEach((e) => {
              delete e._isClean;
              set(e, path, isIndeterminate ? true : !isChecked);
            });
          });
        },
        indeterminate: isIndeterminate,
        checked: isChecked
      }
    );
  }
  const columnTitleTextified = getTextFromEl(columnTitle);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...description && {
        "data-tip": `<div>
          <strong>${columnTitle}:</strong> <br>
          ${description} ${isUnique ? "<br>Must be unique" : ""}</div>`
      },
      onContextMenu: (e) => {
        if (!withDisplayOptions) {
          return;
        }
        e.preventDefault();
        e.persist();
        showContextMenu(
          [
            dragNoticeEl,
            {
              text: "Hide Column",
              disabled: onlyOneVisibleColumn,
              icon: "eye-off",
              onClick: () => {
                updateColumnVisibility({
                  shouldShow: false,
                  path
                });
              }
            },
            {
              text: "Hide Other Columns",
              icon: "eye-off",
              onClick: () => {
                updateColumnVisibility({
                  shouldShow: false,
                  paths: schema.fields.map((c) => c.path).filter(Boolean).filter((p) => p !== path)
                });
              }
            },
            {
              text: "Reset Column Visibilities",
              icon: "reset",
              onClick: () => {
                resetDefaultVisibility();
              }
            },
            {
              text: "Table Display Options",
              icon: "cog",
              onClick: () => {
                e.target.closest(".data-table-container")?.querySelector(".tg-table-display-options")?.click();
              }
            }
          ],
          void 0,
          e
        );
      },
      "data-test": columnTitleTextified,
      "data-path": path,
      "data-copy-text": columnTitleTextified,
      "data-copy-json": JSON.stringify({
        __strVal: columnTitleTextified,
        __isHeaderCell: true
      }),
      className: classNames("tg-react-table-column-header", {
        "sort-active": sortUp || sortDown
      }),
      children: [
        columnHeader && columnHeader({
          recordIdToIsVisibleMap,
          setRecordIdToIsVisibleMap,
          addFilters,
          column,
          compact,
          currentParams,
          entities,
          extraCompact,
          filters,
          formName,
          isCellEditable,
          isLocalCall,
          order,
          removeSingleFilter,
          setNewParams,
          setOrder,
          updateEntitiesHelper,
          withFilter,
          withSort
        }),
        columnTitleTextified && !noTitle && /* @__PURE__ */ jsxs(Fragment, { children: [
          maybeCheckbox,
          /* @__PURE__ */ jsxs(
            "span",
            {
              title: columnTitleTextified,
              className: classNames({
                "tg-react-table-name": true,
                "no-data-tip": !!description
              }),
              style: {
                ...description && { fontStyle: "italic" },
                display: "inline-block"
              },
              children: [
                renderTitleInner ? renderTitleInner : columnTitle,
                " "
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: { display: "flex", marginLeft: "auto", alignItems: "center" },
            children: [
              withSort && !disableSorting && /* @__PURE__ */ jsxs("div", { className: "tg-sort-arrow-container", children: [
                /* @__PURE__ */ jsx(
                  Icon,
                  {
                    "data-tip": "Sort Z-A (Hold shift to sort multiple columns)",
                    icon: "chevron-up",
                    className: classNames({
                      active: sortUp
                    }),
                    color: sortUp ? "#106ba3" : void 0,
                    iconSize: extraCompact ? 10 : 12,
                    onClick: (e) => {
                      setOrder("-" + ccDisplayName, sortUp, e.shiftKey);
                    }
                  }
                ),
                /* @__PURE__ */ jsx(
                  Icon,
                  {
                    "data-tip": "Sort A-Z (Hold shift to sort multiple columns)",
                    icon: "chevron-down",
                    className: classNames({
                      active: sortDown
                    }),
                    color: sortDown ? "#106ba3" : void 0,
                    iconSize: extraCompact ? 10 : 12,
                    onClick: (e) => {
                      setOrder(ccDisplayName, sortDown, e.shiftKey);
                    }
                  }
                )
              ] }),
              withFilter && !disableFiltering && /* @__PURE__ */ jsx(
                ColumnFilterMenu,
                {
                  formName,
                  FilterMenu,
                  filterActiveForColumn,
                  addFilters,
                  removeSingleFilter,
                  currentFilter,
                  filterOn: ccDisplayName,
                  dataType: columnDataType,
                  schemaForField: column,
                  currentParams,
                  setNewParams,
                  compact,
                  extraCompact
                }
              )
            ]
          }
        )
      ]
    }
  );
};
const renderCheckboxHeader = ({
  change,
  entities,
  isEntityDisabled,
  isSingleSelect,
  noDeselectAll,
  noSelect,
  noUserSelect = false,
  onDeselect,
  onMultiRowSelect,
  onRowSelect,
  onSingleRowSelect,
  reduxFormSelectedEntityIdMap
}) => {
  const checkedRows = getSelectedRowsFromEntities(
    entities,
    reduxFormSelectedEntityIdMap
  );
  const checkboxProps = {
    checked: false,
    indeterminate: false
  };
  const notDisabledEntityCount = entities.reduce((acc, e) => {
    return isEntityDisabled(e) ? acc : acc + 1;
  }, 0);
  if (checkedRows.length === notDisabledEntityCount) {
    checkboxProps.checked = notDisabledEntityCount !== 0;
  } else {
    if (checkedRows.length) {
      checkboxProps.indeterminate = true;
    }
  }
  return !isSingleSelect ? /* @__PURE__ */ jsx(
    Checkbox,
    {
      name: "checkBoxHeader",
      disabled: noSelect || noUserSelect,
      onChange: () => {
        const newIdMap = cloneDeep(reduxFormSelectedEntityIdMap) || {};
        entities.forEach((entity, i) => {
          if (isEntityDisabled(entity)) return;
          const entityId = getIdOrCodeOrIndex(entity, i);
          if (checkboxProps.checked) {
            delete newIdMap[entityId];
          } else {
            newIdMap[entityId] = { entity };
          }
        });
        finalizeSelection({
          idMap: newIdMap,
          entities,
          props: {
            onDeselect,
            onSingleRowSelect,
            onMultiRowSelect,
            noDeselectAll,
            onRowSelect,
            noSelect,
            change
          }
        });
      },
      ...checkboxProps
    }
  ) : null;
};
export const useColumns = ({
  addFilters,
  cellRenderer,
  columns,
  resetDefaultVisibility,
  currentParams,
  compact,
  hideExpandSubCompColumn,
  editingCell,
  editingCellSelectAll,
  entities,
  expandedEntityIdMap,
  extraCompact,
  filters,
  formName,
  getCellHoverText,
  isCellEditable,
  isEntityDisabled,
  isLocalCall,
  isSimple,
  isSingleSelect,
  isSelectionARectangle,
  noDeselectAll,
  noSelect,
  noUserSelect,
  onDeselect,
  onMultiRowSelect,
  onRowClick,
  onRowSelect,
  onSingleRowSelect,
  order,
  primarySelectedCellId,
  reduxFormCellValidation,
  reduxFormSelectedEntityIdMap,
  refocusTable,
  removeSingleFilter = noop,
  schema,
  selectedCells,
  setExpandedEntityIdMap,
  setNewParams,
  updateColumnVisibility,
  setOrder = noop,
  setSelectedCells,
  shouldShowSubComponent,
  startCellEdit,
  SubComponent,
  tableRef,
  updateEntitiesHelper,
  updateValidation,
  withCheckboxes,
  withExpandAndCollapseAllButton,
  withFilter: _withFilter,
  withSort = true,
  recordIdToIsVisibleMap,
  setRecordIdToIsVisibleMap,
  withDisplayOptions,
  getCheckboxGroupId
}) => {
  const dispatch = useDispatch();
  const change = useCallback(
    (...args) => dispatch(_change(formName, ...args)),
    [dispatch, formName]
  );
  const withFilter = _withFilter === void 0 ? !isSimple : _withFilter;
  const onDragEnd = useCallback(
    (cellsToSelect) => {
      const [primaryRowId, primaryCellPath] = primarySelectedCellId.split(":");
      const pathToField = getFieldPathToField(schema);
      const { selectedPaths, selectionGrid } = isSelectionARectangle();
      let allSelectedPaths = selectedPaths;
      if (!allSelectedPaths) {
        allSelectedPaths = [primaryCellPath];
      }
      updateEntitiesHelper(entities, (entities2) => {
        let newSelectedCells;
        if (selectedPaths) {
          newSelectedCells = {
            ...selectedCells
          };
        } else {
          newSelectedCells = {
            [primarySelectedCellId]: PRIMARY_SELECTED_VAL
          };
        }
        const newCellValidate = {
          ...reduxFormCellValidation
        };
        const entityMap = getEntityIdToEntity(entities2);
        const { e: selectedEnt } = entityMap[primaryRowId];
        const firstCellToSelectRowIndex = entityMap[cellsToSelect[0]?.split(":")[0]]?.i;
        const pathToIndex = getFieldPathToIndex(schema);
        allSelectedPaths.forEach((selectedPath) => {
          const column = pathToField[selectedPath];
          const selectedCellVal = getCellVal(selectedEnt, selectedPath, column);
          const cellIndexOfSelectedPath = pathToIndex[selectedPath];
          let incrementStart;
          let incrementPrefix;
          let incrementPad = 0;
          if (column.type === "string" || column.type === "number") {
            const cellNumStr = getNumberStrAtEnd(selectedCellVal);
            const cellNum = Number(cellNumStr);
            const entityAbovePrimaryCell = entities2[entityMap[primaryRowId].i - 1];
            if (cellNumStr !== null && !isNaN(cellNum)) {
              if (entityAbovePrimaryCell && (!selectionGrid || selectionGrid.length <= 1)) {
                const cellAboveVal = get(
                  entityAbovePrimaryCell,
                  selectedPath,
                  ""
                );
                const cellAboveNumStr = getNumberStrAtEnd(cellAboveVal);
                const cellAboveNum = Number(cellAboveNumStr);
                if (!isNaN(cellAboveNum)) {
                  const isIncremental = cellNum - cellAboveNum === 1;
                  if (isIncremental) {
                    const cellTextNoNum = stripNumberAtEnd(selectedCellVal);
                    const sameText = stripNumberAtEnd(cellAboveVal) === cellTextNoNum;
                    if (sameText) {
                      incrementStart = cellNum + 1;
                      incrementPrefix = cellTextNoNum || "";
                      if (cellNumStr && cellNumStr.startsWith("0")) {
                        incrementPad = cellNumStr.length;
                      }
                    }
                  }
                }
              }
              if (incrementStart === void 0) {
                const draggingDown = firstCellToSelectRowIndex > selectionGrid?.[0][0].rowIndex;
                if (selectedPaths && draggingDown) {
                  let checkIncrement;
                  let prefix;
                  let maybePad;
                  const allAreIncrementing = selectionGrid.every((row) => {
                    const cellInfo = row[cellIndexOfSelectedPath];
                    if (!cellInfo) return false;
                    const { cellId } = cellInfo;
                    const [rowId] = cellId.split(":");
                    const cellVal = getCellVal(
                      entityMap[rowId].e,
                      selectedPath,
                      pathToField[selectedPath]
                    );
                    const cellNumStr2 = getNumberStrAtEnd(cellVal);
                    const cellNum2 = Number(cellNumStr2);
                    const cellTextNoNum = stripNumberAtEnd(cellVal);
                    if (cellNumStr2?.startsWith("0")) {
                      maybePad = cellNumStr2.length;
                    }
                    if (cellTextNoNum && !prefix) {
                      prefix = cellTextNoNum;
                    }
                    if (cellTextNoNum && prefix !== cellTextNoNum) {
                      return false;
                    }
                    if (!isNaN(cellNum2)) {
                      if (!checkIncrement) {
                        checkIncrement = cellNum2;
                        return true;
                      } else {
                        return ++checkIncrement === cellNum2;
                      }
                    } else {
                      return false;
                    }
                  });
                  if (allAreIncrementing) {
                    incrementStart = checkIncrement + 1;
                    incrementPrefix = prefix || "";
                    incrementPad = maybePad;
                  }
                }
              }
            }
          }
          let firstSelectedCellRowIndex;
          if (selectionGrid) {
            selectionGrid[0].some((cell) => {
              if (cell) {
                firstSelectedCellRowIndex = cell.rowIndex;
                return true;
              }
              return false;
            });
          }
          cellsToSelect.forEach((cellId) => {
            const [rowId, cellPath] = cellId.split(":");
            if (cellPath !== selectedPath) return;
            newSelectedCells[cellId] = true;
            const { e: entityToUpdate, i: rowIndex } = entityMap[rowId] || {};
            if (entityToUpdate) {
              delete entityToUpdate._isClean;
              let newVal;
              if (incrementStart !== void 0) {
                const num = incrementStart++;
                newVal = incrementPrefix + padStart(num, incrementPad, "0");
              } else {
                if (selectionGrid && selectionGrid.length > 1) {
                  const draggingDown = rowIndex > firstSelectedCellRowIndex;
                  const cellIndex = pathToIndex[cellPath];
                  let cellIdToCopy;
                  if (draggingDown) {
                    const { cellId: cellId2 } = selectionGrid[(rowIndex - firstSelectedCellRowIndex) % selectionGrid.length].find((g) => g && g.cellIndex === cellIndex);
                    cellIdToCopy = cellId2;
                  } else {
                    const lastIndexInGrid = selectionGrid[selectionGrid.length - 1][0].rowIndex;
                    const { cellId: cellId2 } = selectionGrid[(rowIndex + lastIndexInGrid + 1) % selectionGrid.length].find((g) => g.cellIndex === cellIndex);
                    cellIdToCopy = cellId2;
                  }
                  const [rowIdToCopy, cellPathToCopy] = cellIdToCopy.split(":");
                  newVal = getCellVal(
                    entityMap[rowIdToCopy].e,
                    cellPathToCopy,
                    pathToField[cellPathToCopy]
                  );
                } else {
                  newVal = selectedCellVal;
                }
              }
              const { error } = editCellHelper({
                entity: entityToUpdate,
                path: cellPath,
                schema,
                newVal
              });
              newCellValidate[cellId] = error;
            }
          });
        });
        updateValidation(entities2, newCellValidate);
        setSelectedCells(newSelectedCells);
      });
    },
    [
      entities,
      isSelectionARectangle,
      primarySelectedCellId,
      reduxFormCellValidation,
      schema,
      selectedCells,
      setSelectedCells,
      updateEntitiesHelper,
      updateValidation
    ]
  );
  const getCopyTextForCell = useCallback(
    (val, row = {}, column = {}) => {
      let text = typeof val !== "string" ? row.value : val;
      const record = row.original;
      if (column.getClipboardData) {
        text = column.getClipboardData(row.value, record, row);
      } else if (column.getValueToFilterOn) {
        text = column.getValueToFilterOn(record);
      } else if (column.render) {
        text = column.render(row.value, record, row, {
          currentParams,
          setNewParams
        });
      } else if (cellRenderer && cellRenderer[column.path]) {
        text = cellRenderer[column.path](row.value, row.original, row, {
          currentParams,
          setNewParams
        });
      } else if (text) {
        text = isValidElement(text) ? text : String(text);
      }
      const getTextFromElementOrLink = (text2) => {
        if (isValidElement(text2)) {
          if (text2.props?.to) {
            return joinUrl(
              window.location.origin,
              window.frontEndConfig?.clientBasePath || "",
              text2.props.to
            );
          } else {
            return getTextFromEl(text2);
          }
        } else {
          return text2;
        }
      };
      text = getTextFromElementOrLink(text);
      if (Array.isArray(text)) {
        let arrText = text.map(getTextFromElementOrLink).join(", ");
        arrText = arrText.replace(/, ,/g, ",");
        text = arrText;
      }
      const stringText = toString(text);
      if (stringText === "[object Object]") return "";
      return stringText;
    },
    [cellRenderer, currentParams, setNewParams]
  );
  const renderCheckboxCell = useCallback(
    (row) => {
      const rowIndex = row.index;
      const checkedRows = getSelectedRowsFromEntities(
        entities,
        reduxFormSelectedEntityIdMap
      );
      const isSelected = checkedRows.some((rowNum) => {
        return rowNum === rowIndex;
      });
      if (rowIndex >= entities.length) {
        return /* @__PURE__ */ jsx("div", {});
      }
      const entity = entities[rowIndex];
      if (getCheckboxGroupId) {
        const currentGroupId = getCheckboxGroupId(entity, rowIndex);
        const previousEntity = entities[rowIndex - 1];
        const previousGroupId = previousEntity ? getCheckboxGroupId(previousEntity, rowIndex - 1) : void 0;
        if (currentGroupId && currentGroupId === previousGroupId) {
          return /* @__PURE__ */ jsx("div", {});
        }
      }
      return /* @__PURE__ */ jsx(
        Checkbox,
        {
          name: `${getIdOrCodeOrIndex(entity, rowIndex)}-checkbox`,
          disabled: noSelect || noUserSelect || isEntityDisabled(entity),
          onClick: (e) => {
            rowClick(e, row, entities, {
              reduxFormSelectedEntityIdMap,
              isSingleSelect,
              noSelect,
              onRowClick,
              isEntityDisabled,
              withCheckboxes,
              onDeselect,
              onSingleRowSelect,
              onMultiRowSelect,
              noDeselectAll,
              onRowSelect,
              change,
              getCheckboxGroupId
            });
          },
          checked: isSelected
        }
      );
    },
    [
      change,
      entities,
      isEntityDisabled,
      isSingleSelect,
      noDeselectAll,
      noSelect,
      noUserSelect,
      onDeselect,
      onMultiRowSelect,
      onRowClick,
      onRowSelect,
      onSingleRowSelect,
      reduxFormSelectedEntityIdMap,
      withCheckboxes,
      getCheckboxGroupId
    ]
  );
  const finishCellEdit = useCallback(
    (cellId, newVal, doNotStopEditing) => {
      const [rowId, path] = cellId.split(":");
      !doNotStopEditing && change("reduxFormEditingCell", null);
      updateEntitiesHelper(entities, (entities2) => {
        const entity = entities2.find((e, i) => {
          return getIdOrCodeOrIndex(e, i) === rowId;
        });
        delete entity._isClean;
        const { error } = editCellHelper({
          entity,
          path,
          schema,
          newVal
        });
        updateValidation(entities2, {
          ...reduxFormCellValidation,
          [cellId]: error
        });
      });
      !doNotStopEditing && refocusTable();
    },
    [
      change,
      entities,
      reduxFormCellValidation,
      refocusTable,
      schema,
      updateEntitiesHelper,
      updateValidation
    ]
  );
  const cancelCellEdit = useCallback(() => {
    change("reduxFormEditingCell", null);
    refocusTable();
  }, [change, refocusTable]);
  if (!columns.length) {
    return columns;
  }
  const columnsToRender = [];
  if (SubComponent) {
    columnsToRender.push({
      ...withExpandAndCollapseAllButton && {
        Header: () => {
          const showCollapseAll = Object.values(expandedEntityIdMap).filter((i) => i).length === entities.length;
          return /* @__PURE__ */ jsx(
            InfoHelper,
            {
              content: showCollapseAll ? "Collapse All" : "Expand All",
              isButton: true,
              minimal: true,
              small: true,
              style: { padding: 2 },
              popoverProps: {
                modifiers: {
                  preventOverflow: { enabled: false },
                  hide: { enabled: false }
                }
              },
              onClick: () => {
                showCollapseAll ? setExpandedEntityIdMap({}) : setExpandedEntityIdMap((prev) => {
                  const newMap = { ...prev };
                  entities.forEach((e) => {
                    newMap[getIdOrCodeOrIndex(e)] = true;
                  });
                  return newMap;
                });
              },
              className: classNames("tg-expander-all"),
              icon: showCollapseAll ? "chevron-down" : "chevron-right"
            }
          );
        }
      },
      show: !hideExpandSubCompColumn,
      expander: true,
      Expander: ({ isExpanded, original: record }) => {
        let shouldShow = true;
        if (shouldShowSubComponent) {
          shouldShow = shouldShowSubComponent(record);
        }
        if (!shouldShow || hideExpandSubCompColumn) return null;
        return /* @__PURE__ */ jsx(
          Button,
          {
            className: classNames(
              "tg-expander",
              Classes.MINIMAL,
              Classes.SMALL
            ),
            icon: isExpanded ? "chevron-down" : "chevron-right"
          }
        );
      }
    });
  }
  if (withCheckboxes) {
    columnsToRender.push({
      Header: renderCheckboxHeader({
        change,
        entities,
        isEntityDisabled,
        isSingleSelect,
        noDeselectAll,
        noSelect,
        noUserSelect,
        onDeselect,
        onMultiRowSelect,
        onRowSelect,
        onSingleRowSelect,
        reduxFormSelectedEntityIdMap
      }),
      Cell: renderCheckboxCell,
      width: 35,
      resizable: false,
      getHeaderProps: () => {
        return {
          className: "tg-react-table-checkbox-header-container",
          immovable: "true"
        };
      },
      getProps: () => {
        return {
          className: "tg-react-table-checkbox-cell-container"
        };
      }
    });
  }
  const tableColumns = flatMap(columns, (column) => {
    if (column.isHidden) {
      return [];
    }
    const tableColumn = {
      ...column,
      Header: RenderColumnHeader({
        onlyOneVisibleColumn: columns.length === 1,
        recordIdToIsVisibleMap,
        setRecordIdToIsVisibleMap,
        column,
        withDisplayOptions,
        isLocalCall,
        filters,
        currentParams,
        order,
        resetDefaultVisibility,
        setOrder,
        withSort,
        schema,
        updateColumnVisibility,
        formName,
        extraCompact,
        withFilter,
        addFilters,
        removeSingleFilter,
        setNewParams,
        compact,
        isCellEditable,
        entities,
        updateEntitiesHelper
      }),
      accessor: column.path,
      getHeaderProps: () => ({
        // needs to be a string because it is getting passed
        // to the dom
        immovable: column.type === "action" || column.immovable ? "true" : "false",
        columnindex: column.columnIndex,
        path: column.path
      })
    };
    const noEllipsis = column.noEllipsis;
    if (column.width) {
      tableColumn.width = column.width;
    }
    if (cellRenderer && cellRenderer[column.path]) {
      tableColumn.Cell = (row) => {
        const val = cellRenderer[column.path](row.value, row.original, row, {
          currentParams,
          setNewParams
        });
        return val;
      };
    } else if (column.render) {
      tableColumn.Cell = (row) => {
        const val = column.render(row.value, row.original, row, {
          recordIdToIsVisibleMap,
          setRecordIdToIsVisibleMap,
          currentParams,
          setNewParams
        });
        return val;
      };
    } else if (column.type === "timestamp") {
      tableColumn.Cell = ({ value }) => {
        return value ? dayjs(value).format("lll") : "";
      };
    } else if (column.type === "color") {
      tableColumn.Cell = ({ value }) => {
        return value ? /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              height: 20,
              width: 40,
              background: value,
              border: "1px solid #182026",
              borderRadius: 5
            }
          }
        ) : "";
      };
    } else if (column.type === "boolean") {
      if (isCellEditable) {
        tableColumn.Cell = ({ value }) => value ? "True" : "False";
      } else {
        tableColumn.Cell = ({ value }) => /* @__PURE__ */ jsx(
          Icon,
          {
            className: classNames({
              [Classes.TEXT_MUTED]: !value
            }),
            icon: value ? "tick" : "cross"
          }
        );
      }
    } else if (column.type === "markdown") {
      tableColumn.Cell = ({ value }) => /* @__PURE__ */ jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], children: value });
    } else {
      tableColumn.Cell = ({ value }) => value;
    }
    const oldFunc = tableColumn.Cell;
    tableColumn.Cell = (...args) => /* @__PURE__ */ jsx(
      RenderCell,
      {
        oldFunc,
        formName,
        getCopyTextForCell,
        column,
        isCellEditable,
        isEntityDisabled,
        finishCellEdit,
        noEllipsis,
        editingCell,
        cancelCellEdit,
        editingCellSelectAll,
        getCellHoverText,
        selectedCells,
        isSelectionARectangle,
        startCellEdit,
        tableRef,
        onDragEnd,
        args
      }
    );
    return tableColumn;
  });
  return columnsToRender.concat(tableColumns);
};
